
import { defineComponent, onMounted, computed } from 'vue';
import SPage from 'PortalDir/components/page/SPage.vue';
import NotFoundData from 'SharedComponents/ui/Images/NotFoundData.vue';
import Filters from 'PortalDir/components/Filters.vue';
import { usePublicNewsList } from 'PortalDir/modules/News/composables/usePublicNewsList';
import { preFetch } from 'quasar/wrappers';
import { PublicNewsApi } from 'SharedDir/backend/api';
import { useDeviceSize } from 'SharedDir/common/composables/useDeviceSize';
import { useNewsStore } from 'stores/news';
import { PaginationParams } from 'SharedDir/declarations/pagination';
import { useLoadingSsr } from 'PortalDir/common/composables/useLoadingSsr';
import { random } from '@packages/toolbox';
import NewsItem from 'PortalDir/modules/News/components/Item.vue';
import NewsSkeletonItem from 'PortalDir/modules/Main/components/News/SkeletonItem.vue';
import { useMeta } from 'quasar';
import { useI18n } from 'vue-i18n';
import { useMetaPortal } from 'SharedDir/common/composables/useMetaPortal';

async function getData(newsStore, ssrContext, queryParam = {}) {
  const { paginationParams, filterParams, fillFromQueryStringSsr } = usePublicNewsList();

  fillFromQueryStringSsr(queryParam, paginationParams, filterParams);

  const params = {
    ...newsStore.pagination,
    ...paginationParams.value,
    ...newsStore.filters,
    ...filterParams.value,
  };
  const response = await PublicNewsApi.getPaginated(params);
  const updatedPaginationParams: PaginationParams<any> = {
    page: response.page,
    rowsPerPage: response.rowsPerPage,
    rowsNumber: response.rowsNumber,
    showDeleted: response.showDeleted,
    sortBy: response.sortBy,
    descending: response.descending,
  };
  newsStore.setNewsList(response.data);
  newsStore.setNewsPagination(updatedPaginationParams);
}

export default defineComponent({
  name: 'NewsPublicList',
  methods: { random },
  components: { NewsSkeletonItem, NewsItem, Filters, SPage, NotFoundData },
  props: {},

  preFetch: preFetch(async ({ store, ssrContext, currentRoute }) => {
    const newsStore = useNewsStore();
    await getData(newsStore, ssrContext, currentRoute?.query || {});
  }),

  setup() {
    const { isLoading, showLoading, loadingStore } = useLoadingSsr();
    const { t } = useI18n();

    const {
      paginationParams,
      filterParams,
      fetch,
      list,
      defaultFilterParams,
      setQueryParams,
      fillFromQueryString,
    } = usePublicNewsList();
    const newsStore = useNewsStore();
    const newsList = computed(() => {
      return newsStore.newsList;
    });

    const pagination = computed(() => {
      return newsStore.pagination || {};
    });

    async function applyFilter() {
      await setQueryParams({ ...filterParams.value, ...pagination.value });
      paginationParams.value = { ...pagination.value };
      await fetch();
      newsStore.setNewsList(list.value);
    }

    function clearFilterParam(param) {
      const defaultValue = defaultFilterParams();
      filterParams.value[param] = defaultValue[param];
    }

    async function proxyInputPagination() {
      await applyFilter();
      window.scrollTo({
        top: 0,
        behavior: 'smooth',
      });
    }

    const maxPagePagination = computed(() => {
      return Math.ceil((pagination.value?.rowsNumber || 1) / (pagination.value?.rowsPerPage || 1));
    });

    onMounted(async () => {
      fillFromQueryString(pagination, filterParams);
      await setQueryParams({ ...filterParams.value, ...pagination.value });
      isLoading.value = false;
    });

    const { clearFullPageUrl } = useMetaPortal();

    useMeta(() => {
      return {
        title: `Новости - ${t('systemType')} ${t('systemShortName')}`,
        meta: {
          description: {
            name: 'description',
            template: () => `Новостная информация ${t('systemFullName')} (${t('systemShortName')})`,
          },
          keywords: {
            name: 'keywords',
            template: () =>
              [
                `${t('systemType')} ${t('systemShortName')}`,
                `${t('systemType')} ${t('systemFullName')}`,
                `Новости ${t('systemShortName')}`,
                `Новости ${t('systemFullName')}`,
                `Новости ${t('regionShortName')}`,
                `Новости ${t('regionFullName')}`,
                `Новостная информация ${t('systemShortName')}`,
                `Новостная информация ${t('systemFullName')}`,
                `Новостная информация ${t('regionShortName')}`,
                `Новостная информация ${t('regionFullName')}`,
              ].join(', '),
          },
        },
        link: {
          canonical: {
            rel: 'canonical',
            href: clearFullPageUrl.value,
          },
        },
      };
    });

    return {
      applyFilter,
      clearFilterParam,
      list,
      paginationParams,
      filterParams,
      defaultFilterParams,
      proxyInputPagination,
      maxPagePagination,
      pagination,
      newsList,
      ...useDeviceSize(),
      isLoading,
      showLoading,
    };
  },
});
